import axios from 'axios';
import { getHtmlFromLexical, getPlainTextFromLexical } from './lexicalParser.js';

const API_URL = 'https://cms.seedr.cc/api';
const CACHE_DURATION = 0.25 * 60 * 1000; // 5 minutes in milliseconds

// Cache structure
const cache = {
  articles: new Map(),
  previews: new Map(), // Shared cache for previews (search, tags, categories)
  categories: new Map(),
  tags: new Map()
};

const cmsClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Helper function to check if cache is valid
const isCacheValid = (timestamp) => {
  return timestamp && (Date.now() - timestamp) < CACHE_DURATION;
};

export async function getArticlesLocal(locale = 'en') {
  const cacheKey = `articles_${locale}`;
  const cached = cache.previews.get(cacheKey);

  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    const response = await cmsClient.get('/articles', {
      params: {
        depth: 2,
        locale,
        where: {
          _status: {
            equals: 'published'
          },
        },
        fields: {
          content: false // Exclude content field
        },
        limit: 100000 // Get all articles
      }
    });

    const articles = response.data.docs.map(article => getArticlePreview(article));
    cache.previews.set(cacheKey, { data: articles, timestamp: Date.now() });
    return articles;
  } catch (error) {
    console.error('Error fetching articles:', error);
    return [];
  }
}

export async function getArticleBySlug(slug, locale = 'en') {
  const cacheKey = `article_${slug}_${locale}`;
  const cached = cache.articles.get(cacheKey);

  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    const response = await cmsClient.get(`/articles`, {
      params: {
        depth: 2,
        locale,
        draft: false,
        fallbackLocale: 'en',
        where: {
          slug: {
            equals: slug
          }
        },
      }
    });

    // Map the full article content
    const article = mapFullArticle(response.data.docs[0]);
    cache.articles.set(cacheKey, { data: article, timestamp: Date.now() });
    return article;
  } catch (error) {
    console.error('Error fetching article by slug:', error);
    return null;
  }
}

export async function getArticleById(id, locale = 'en', draft = false) {
  // Only use cache for published articles, not drafts
  if (!draft) {
    const cacheKey = `article_${id}_${locale}`;
    const cached = cache.articles.get(cacheKey);

    if (cached && isCacheValid(cached.timestamp)) {
      return cached.data;
    }
  }

  try {
    const response = await cmsClient.get(`/articles`, {
      params: {
        depth: 2,
        locale,
        fallbackLocale: 'en',
        draft,
        where: {
          id: {
            equals: id
          }
        },
      }
    });

    // Map the full article content
    const article = await mapFullArticle(response.data.docs[0]);
    
    // Only cache published articles
    if (!draft) {
      const cacheKey = `article_${id}_${locale}`;
      cache.articles.set(cacheKey, { data: article, timestamp: Date.now() });
    }
    
    return article;
  } catch (error) {
    console.error('Error fetching article by id:', error);
    return null;
  }
}

export async function getRelatedArticles(articleId, categorySlug, tags, locale = 'en', limit = 3) {
  const cacheKey = `related_${articleId}_${locale}`;
  const cached = cache.previews.get(cacheKey);

  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    const response = await cmsClient.get('/articles', {
      params: {
        depth: 1,
        locale,
        draft: false,
        where: {
          or: [
            { 'category.slug': { equals: categorySlug } },
            { 'tags.slug': { in: tags.map(t => t.slug) } }
          ],
          id: { not_equals: articleId }
        },
        limit,
        fields: {
          content: false // Exclude content field
        }
      }
    });

    // Map directly to our expected format without processing preview
    const articles = response.data.docs.map(article => getArticlePreview(article))

    cache.previews.set(cacheKey, { data: articles, timestamp: Date.now() });
    return articles;
  } catch (error) {
    console.error('Error fetching related articles:', error);
    return [];
  }
}
export async function getArticlesByCategory(categories, limit = 6, page = 1, locale = 'en') {
  // Normalize categories to array
  const categoryArray = Array.isArray(categories) ? categories : [categories];

  // Create a combined cache key for all categories
  const categoriesKey = categoryArray.sort().join('_');
  const cacheKey = `categories_${categoriesKey}_${locale}_${page}_${limit}`;
  const cached = cache.previews.get(cacheKey);

  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    // Create promises for each category query
    const categoryPromises = categoryArray.map(category =>
      cmsClient.get('/articles', {
        params: {
          depth: 1,
          locale,
          draft:false,
          where: {
            'category.slug': {
              equals: category
            }
          },
          fields: {
            content: false // Exclude content field
          },
          limit: limit,
          page: page,
        }
      })
    );

    // Execute all queries in parallel
    const responses = await Promise.all(categoryPromises);

    // Process responses for each category
    const result = responses.reduce((acc, response, index) => {
      const category = categoryArray[index];
      const articles = response.data.docs.map(article => getArticlePreview(article));

      acc[category] = {
        articles,
        pagination: {
          totalDocs: response.data.totalDocs,
          limit: response.data.limit,
          totalPages: response.data.totalPages,
          page: response.data.page,
          hasNextPage: response.data.hasNextPage,
          hasPrevPage: response.data.hasPrevPage,
          nextPage: response.data.nextPage,
          prevPage: response.data.prevPage
        }
      };

      return acc;
    }, {});

    // Add metadata about the complete result
    const completeResult = {
      categoryResults: result,
      metadata: {
        categories: categoryArray,
        totalCategories: categoryArray.length,
        page,
        limit,
        locale
      }
    };

    // Cache the complete result
    cache.previews.set(cacheKey, {
      data: completeResult,
      timestamp: Date.now()
    });

    return completeResult;

  } catch (error) {
    console.error('Error fetching articles by categories:', error);

    // Create error response with empty data for each category
    const errorResult = categoryArray.reduce((acc, category) => {
      acc[category] = {
        articles: [],
        pagination: {
          totalDocs: 0,
          limit,
          totalPages: 0,
          page,
          hasNextPage: false,
          hasPrevPage: false,
          nextPage: null,
          prevPage: null
        }
      };
      return acc;
    }, {});

    return {
      categoryResults: errorResult,
      metadata: {
        categories: categoryArray,
        totalCategories: categoryArray.length,
        page,
        limit,
        locale
      }
    };
  }
}

// Categories and Tags with caching
export async function getAllCategories(locale = 'en') {
  const cacheKey = `categories_${locale}`;
  const cached = cache.categories.get(cacheKey);

  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    const response = await cmsClient.get('/categories', {
      params: {
        depth: 1,
        // order by priority ascending
        sort: 'priority',
        locale
      }
    });

    const categories = response.data.docs.map(category => ({
      name: category.name,
      slug: category.slug
    }));

    cache.categories.set(cacheKey, { data: categories, timestamp: Date.now() });
    return categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
}

export async function getAllTags(locale = 'en') {
  const cacheKey = `tags_${locale}`;
  const cached = cache.tags.get(cacheKey);

  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    const response = await cmsClient.get('/tags', {
      params: {
        depth: 1,
        locale
      }
    });

    const tags = response.data.docs.map(tag => ({
      name: tag.name,
      slug: tag.slug
    }));

    cache.tags.set(cacheKey, { data: tags, timestamp: Date.now() });
    return tags;
  } catch (error) {
    console.error('Error fetching tags:', error);
    return [];
  }
}

function getArticlePreview(article) {
  return {
    id: article.id,
    title: article.title,
    preview: article.preview,
    slug: article.slug,
    date: article.createdAt,
    category: article.category?.name || '',
    categorySlug: article.category?.slug || '',
    tags: article.tags?.map(tag => ({ name: tag.name, slug: tag.slug })) || [],
    image: article.image?.url || 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==',
    imageSizes: article.image?.sizes || {},
    featuredImage: article.featuredImage?.url || '',
    featuredImageSizes: article.featuredImage?.sizes || {},
    author: article.author
  }
}

export async function getFeaturedArticles(locale = 'en', limit = 3) {
  const cacheKey = `featured_${locale}`;
  const cached = cache.previews.get(cacheKey);

  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    const response = await cmsClient.get('/articles', {
      params: {
        depth: 1,
        locale,
        where: {
          _status: {
            equals: 'published'
          },
          isFeatured: {
            equals: true
          }
        },
        limit: limit,
        sort: '-createdAt',
        fields: {
          content: false // Exclude content field
        }
      }
    });

    const articles = response.data.docs.map(article => getArticlePreview(article));

    cache.previews.set(cacheKey, { data: articles, timestamp: Date.now() });
    return articles;
  } catch (error) {
    console.error('Error fetching featured articles:', error);
    return [];
  }
}

export async function getArticlesByTag(tag, locale = 'en') {
  const cacheKey = `tag_${tag}_${locale}`;
  const cached = cache.previews.get(cacheKey);

  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    const response = await cmsClient.get('/articles', {
      params: {
        depth: 1,
        locale,
        where: {
          'tags.slug': {
            equals: tag
          },
          _status: {
            equals: 'published'
          }
        },
        fields: {
          content: false // Exclude content field
        }
      }
    });

    // Map directly to our expected format using the preview field
    const articles = response.data.docs.map(article => getArticlePreview(article))

    cache.previews.set(cacheKey, { data: articles, timestamp: Date.now() });
    return articles;
  } catch (error) {
    console.error('Error fetching articles by tag:', error);
    return [];
  }
}

// For full article views
function mapFullArticle(article) {
  if (!article) return null;
  const plainText = getPlainTextFromLexical(article.content);
  const html = getHtmlFromLexical(article.content);
  
  return {
    ...getArticlePreview(article),
    content: article.content,
    contentHtml: html,
    contentText: plainText,
  };
}


export async function getPaginatedArticles(page = 1, limit = 6, locale = 'en') {
  const cacheKey = `articles_page_${page}_${limit}_${locale}`;
  const cached = cache.previews.get(cacheKey);

  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    const response = await cmsClient.get('/articles', {
      params: {
        depth: 1,
        locale,
        page,
        limit,
        sort: '-createdAt',
        where: {
          _status: {
            equals: 'published'
          }
        },
        fields: {
          content: false // Exclude only the content field
        }
      }
    });

    const articles = response.data.docs.map(article => getArticlePreview(article));

    const result = {
      articles,
      totalPages: Math.ceil(response.data.totalDocs / limit),
      hasNextPage: page < Math.ceil(response.data.totalDocs / limit),
      totalArticles: response.data.totalDocs
    };

    cache.previews.set(cacheKey, {
      data: result,
      timestamp: Date.now()
    });

    return result;
  } catch (error) {
    console.error('Error fetching paginated articles:', error);
    return {
      articles: [],
      totalPages: 0,
      hasNextPage: false,
      totalArticles: 0
    };
  }
}

export async function searchArticles(query, locale = 'en') {
  const cacheKey = `search_${query}_${locale}`;
  const cached = cache.previews.get(cacheKey);

  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }

  try {
    const response = await cmsClient.get('/articles', {
      params: {
        depth: 1,
        locale,
        where: {
          or: [
            { title: { contains: query } },
            { preview: { contains: query } }  // Search in preview instead of content
          ],
          _status: {
            equals: 'published'
          }
        }
      }
    });

    const articles = response.data.docs.map(article => getArticlePreview(article));
    cache.previews.set(cacheKey, { data: articles, timestamp: Date.now() });
    return articles;
  } catch (error) {
    console.error('Error searching articles:', error);
    return [];
  }
}

export async function searchPages(query) {
  try {
    const response = await cmsClient.get('/pages', {
      params: {
        where: {
          and: [
            {
              or: [
                { title: { contains: query } },
                { description: { contains: query } },
                { content: { contains: query } }
              ]
            },
            { isActive: { equals: true } }
          ]
        },
        limit: 10
      }
    });
    return response.data.docs;
  } catch (error) {
    console.error('Error searching pages:', error);
    return [];
  }
}
